/* eslint-disable @typescript-eslint/no-magic-numbers */

import {
  AfterViewInit,
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  ElementRef,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
} from '@angular/core';
import {
  CalendarDateFormatter,
  CalendarDayViewBeforeRenderEvent,
  CalendarEvent as NgCalendarEvent,
  CalendarEventTimesChangedEvent,
  CalendarMonthViewBeforeRenderEvent,
  CalendarWeekViewBeforeRenderEvent,
} from 'angular-calendar';
import { ViewPeriod, WeekDay, WeekViewHourSegment } from 'calendar-utils';
import { isEqual } from 'lodash-es';
import moment from 'moment';
import { BehaviorSubject } from 'rxjs';
import { TranslationKeys } from '@nimbus-library/features/localization/models/enums/translation-keys.enum';
import { IOverlayFieldMeta, OverlayFieldType } from '../../models/overlay-entity/i-overlay-field-meta.model';
import { ICONS } from '../../resources/icons';
import { RecurrentEventsService } from '../../services/calendar/recurrent-events.service';
import { NimbusCalendarDateFormatter } from './calendar-date-formatter';

export enum CalendarView {
  Month = 'month',
  Week = 'week',
  Day = 'day',
}

export enum CalendarTooltipItem {
  Subject = 'subject',
  Location = 'location',
  Start = 'start',
  End = 'end',
}

export interface CalendarEvent {
  id?: string | number;
  dataItem?: unknown;
  start: Date;
  startTimezone?: string;
  end: Date;
  endTimezone?: string;
  isAllDay?: boolean;
  title: string;
  description?: string;
  recurrenceRule?: string;
  recurrenceId?: unknown;
  recurrenceExceptions?: moment.Moment[];
  location: string;
  status: string;
}

export interface CalendarSegment {
  start: Date;
  end: Date;
  isAllDay?: boolean;
}

export interface EventChangeEvent {
  start: Date;
  oldStart: Date;
  end: Date;
  oldEnd: Date;
  event: CalendarEvent;
}

export interface RequestEditEvent {
  parent?: CalendarEvent;
  event: CalendarEvent;
}

export type RequestDeleteEvent = RequestEditEvent;

@Component({
  standalone: false,
  selector: 'nimbus-calendar',
  templateUrl: './calendar.component.html',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [
    {
      provide: CalendarDateFormatter,
      useClass: NimbusCalendarDateFormatter,
    },
  ],
})
export class CalendarComponent implements OnChanges, AfterViewInit {
  @Input() public view: CalendarView = CalendarView.Month;
  @Input() public viewDate: Date = new Date();
  @Input() public events: CalendarEvent[] = [];
  @Input() public resizable: boolean = false;
  @Input() public draggable: boolean = false;
  @Input() public dayStartHour: number = 0;
  @Input() public dayEndHour: number = 24;
  @Input() public editable: boolean = false;
  @Input() public hideLocation: boolean = false;

  @Output() public hourSegmentClicked: EventEmitter<CalendarSegment> = new EventEmitter<CalendarSegment>();
  @Output() public eventChanged: EventEmitter<EventChangeEvent> = new EventEmitter<EventChangeEvent>();
  @Output() public removeEvent: EventEmitter<RequestDeleteEvent> = new EventEmitter<RequestDeleteEvent>();
  @Output() public editEvent: EventEmitter<{ parent?: CalendarEvent; event: Partial<CalendarEvent> }> = new EventEmitter<{
    parent?: CalendarEvent;
    event: CalendarEvent;
  }>();
  @Output() public selectedDateChange: EventEmitter<Date> = new EventEmitter<Date>();

  private viewPeriod: ViewPeriod;

  public calendarEvents$: BehaviorSubject<NgCalendarEvent[]> = new BehaviorSubject<NgCalendarEvent[]>([]);
  public tooltipMetadataConfiguration: { [key in CalendarTooltipItem]: IOverlayFieldMeta } = {
    [CalendarTooltipItem.Subject]: { translation: TranslationKeys.CalendarSubject, type: OverlayFieldType.Text },
    [CalendarTooltipItem.Location]: { translation: TranslationKeys.CalendarLocation, type: OverlayFieldType.Text },
    [CalendarTooltipItem.Start]: { translation: TranslationKeys.CalendarStart, type: OverlayFieldType.Text },
    [CalendarTooltipItem.End]: { translation: TranslationKeys.CalendarEnd, type: OverlayFieldType.Text },
  };

  public ICONS = ICONS;
  public TranslationKeys = TranslationKeys;

  public constructor(
    private readonly changeDetectorRef: ChangeDetectorRef,
    private readonly container: ElementRef<HTMLElement>,
    private readonly recurrentEventsService: RecurrentEventsService,
  ) {}

  public ngOnChanges(changes: SimpleChanges): void {
    if (changes.events && !isEqual(changes.events.currentValue, changes.events.previousValue)) {
      this.calculateEvents();
    }

    if (changes.view) {
      this.scrollToCurrentView();
    }
  }
  public ngAfterViewInit(): void {
    this.scrollToCurrentView();
  }
  public updateCalendarEvents(
    viewRender: CalendarMonthViewBeforeRenderEvent | CalendarWeekViewBeforeRenderEvent | CalendarDayViewBeforeRenderEvent,
  ): void {
    if (
      !this.viewPeriod ||
      !moment(this.viewPeriod.start).isSame(viewRender.period.start) ||
      !moment(this.viewPeriod.end).isSame(viewRender.period.end)
    ) {
      this.viewPeriod = viewRender.period;

      this.calculateEvents();

      this.changeDetectorRef.detectChanges();
    }
  }

  public dayClicked({ date }: { date: Date; events?: CalendarEvent[] }): void {
    this.selectedDateChange.emit(date);
  }

  public onHourSegmentClicked(segment: WeekViewHourSegment, ev: Event): void {
    ev.preventDefault();
    ev.stopPropagation();

    this.hourSegmentClicked.emit({ start: segment.date, end: moment(segment.date).add(30, 'minutes').toDate(), isAllDay: false });
  }

  public onAllDayClicked(day: WeekDay, ev: Event): void {
    ev.preventDefault();
    ev.stopPropagation();
    this.hourSegmentClicked.emit({
      start: moment(day.date).startOf('day').toDate(),
      end: moment(day.date).endOf('day').toDate(),
      isAllDay: true,
    });
  }

  public onEventTimesChanged({ event, newStart, newEnd }: CalendarEventTimesChangedEvent): void {
    const originalEvent = this.events.find((e) => e.id === event.id);

    if (+newStart > +newEnd) {
      return;
    }

    if (+event.end === +newEnd && +event.start === +newStart) {
      return;
    }

    this.eventChanged.emit({
      start: newStart,
      oldStart: event.start,
      end: newEnd,
      oldEnd: event.end,
      event: originalEvent,
    });

    event.start = newStart;
    event.end = newEnd;
  }

  public onEventClicked({ event, sourceEvent }: { event: NgCalendarEvent<unknown>; sourceEvent: Event }): void {
    // Ignore (double-)clicks that originate on an action button (e.g. delete X).
    // dblclick is a separate event from click and is not affected by the
    // stopPropagation() applied inside the action's onClick handler.
    if ((sourceEvent.target as HTMLElement)?.closest('.cal-event-action')) {
      return;
    }

    sourceEvent.stopPropagation();
    sourceEvent.preventDefault();
    const originalEvent = this.events.find((e) => e.id === event.id);
    this.editEvent.emit({ parent: originalEvent, event: { ...event } });
  }

  public onClick(ev: MouseEvent): void {
    ev.stopPropagation();
    ev.preventDefault();
  }

  public scrollToCurrentView(): void {
    if (this.view === CalendarView.Week || CalendarView.Day) {
      const minutesSinceStartOfDay = moment().startOf('hour').diff(moment().startOf('day'), 'minutes');
      const headerHeight = this.view === CalendarView.Week ? 60 : 30;
      this.container.nativeElement.scrollTop =
        minutesSinceStartOfDay + headerHeight - (this.container.nativeElement.getBoundingClientRect().height / 2 - headerHeight);
    }
  }

  private calculateEvents(): void {
    this.calendarEvents$.next(
      this.events.reduce((calendarEvents, e) => {
        if (e.recurrenceRule) {
          calendarEvents.push(
            ...this.getRecurrentEvents(
              e,
              this.viewPeriod || {
                start: moment(this.viewDate).startOf('month').toDate(),
                end: moment(this.viewDate).endOf('month').toDate(),
                events: [],
              },
            ),
          );
        } else {
          const allDayTransformed = !moment(e.end).isSame(e.start, 'day');
          calendarEvents.push({
            id: e.id,
            title: e.title,
            start: e.start,
            end: e.end,
            cssClass: e.status,
            allDay: e.isAllDay || allDayTransformed,
            resizable: {
              afterEnd: this.resizable,
              beforeStart: this.resizable,
            },
            meta: {
              isRecurrent: e.recurrenceId,
              recurrentException: e.recurrenceId,
              showDateInHeader: allDayTransformed && !e.isAllDay,
              location: e.location,
            },
            draggable: this.draggable,
            actions: this.editable
              ? [
                  {
                    label: '<i class="icon-nimbus-font-light icon-nimbus-times"></i>',
                    id: TranslationKeys.GeneralDelete,
                    onClick: ({ event, sourceEvent }): void => {
                      sourceEvent.stopPropagation();
                      sourceEvent.preventDefault();
                      const originalEvent = this.events.find((ev) => ev.id === event.id);
                      this.removeEvent.emit({ parent: originalEvent, event: e });
                    },
                  },
                ]
              : [],
          });
        }

        return calendarEvents;
      }, [] as NgCalendarEvent[]),
    );
  }

  private getRecurrentEvents(e: CalendarEvent, period: ViewPeriod): NgCalendarEvent[] {
    return (
      this.recurrentEventsService.getEventsFromRecurrentRule(
        e.recurrenceRule,
        e.recurrenceExceptions,
        moment(e.start).toDate(),
        moment(period.end).add(1, 'day').toDate(),
        e.startTimezone,
      ) || []
    )
      .map((date) => {
        const diff = e.end.getTime() - e.start.getTime();
        const duration = moment.duration({ hours: 1 });
        const start = moment(date);
        let end = moment(start).add(diff, 'milliseconds');

        // handle winter -> summer time (see https://en.wikipedia.org/wiki/Daylight_saving_time_by_country)
        // time is changed at 2am to 3am (for EU, in some countries it is differently)
        // if event is overlapped with this period we need to subtract 1 hour
        // because diff is applied as is and event displayed incorrectly
        if (!start.isDST() && end.isDST()) {
          end = moment(end).subtract(duration);
        }

        // handle summer -> winter time (see https://en.wikipedia.org/wiki/Daylight_saving_time_by_country)
        // time is changed at 3am to 2am (for EU, in some countries it is differently)
        // if event is overlapped with this period we need to add 1 hour
        // because diff is applied as is and event displayed incorrectly
        if (start.isDST() && !end.isDST()) {
          end = moment(end).add(duration);
        }

        return { end, start };
      })
      .filter(({ start }) => start.isSameOrAfter(moment(e.start).startOf('day')))
      .map(({ end, start }) => {
        const allDayTransformed = !moment(e.end).isSame(e.start, 'day');

        return {
          id: e.id,
          title: e.title,
          start: start.toDate(),
          end: end.toDate(),
          cssClass: e.status,
          allDay: e.isAllDay || allDayTransformed,
          meta: { isRecurrent: true, showDateInHeader: allDayTransformed && !e.isAllDay, location: e.location },
          resizable: {
            afterEnd: this.resizable,
            beforeStart: this.resizable,
          },

          draggable: this.draggable,
          actions: this.editable
            ? [
                {
                  label: '<i class="icon-nimbus-font-light icon-nimbus-times"></i>',
                  onClick: ({ event, sourceEvent }): void => {
                    sourceEvent.stopPropagation();
                    sourceEvent.preventDefault();
                    const originalEvent = this.events.find((ev) => ev.id === event.id);
                    this.removeEvent.emit({ parent: originalEvent, event: { ...event } as CalendarEvent });
                  },
                },
              ]
            : [],
        };
      });
  }
}
